import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import type { IApprovalAuthority } from '@app/land-hold/approval-authority/models/ApprovalAuthority';
import { ApprovalAuthority } from '@app/land-hold/approval-authority/models/ApprovalAuthority';
import type { FormOptions } from '@shared/types/Form';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';

export function useApprovalAuthorityRepository<T extends IApprovalAuthority = IApprovalAuthority>() {
    const command = () => {
        const hasError = useInjectErrorState();
        const store = (data: T, options?: FormOptions) => {
            const form = useForm<T>(data).precognition(
                ApprovalAuthority.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(ApprovalAuthority.routes().update, {
                        id,
                    }),
                    method: ApprovalAuthority.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(ApprovalAuthority.routes().destroy, {
                        id,
                    }),
                    method: ApprovalAuthority.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(ApprovalAuthority.routes().show, { id }) };
        };

        const list = () => {
            const { data: approvalAuthorities, processing: fetchingApprovalAuthorities } = useFetchListData<ApprovalAuthority>(useRoute().build(ApprovalAuthority.routes().index), 'appSection@approvalAuthority::IndexApprovalAuthoritiesPage', (payload) => ApprovalAuthority.from(payload));
            return { approvalAuthorities, fetchingApprovalAuthorities };
        };

        return {
            show,
            list,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
